import {useEffect, useRef, useState} from 'react';
import {
  Alert,
  Button,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {useHistory} from 'react-router-dom';
import moment from 'moment';

import {DiscountCodeService} from './DiscountCodeService';
import {CreateDiscountCodeModal} from './components/CreateDiscountCodeModal';
import {useDiscountCodeData} from './hooks/useDiscountCodeData';
import {useTablePagination} from '../../hooks/useTablePagination';
import type {NewDiscountCode} from '@PikaDoApp/npm-pikado-logic/dist/modules/discountCode/graphql/discountCode.interface';

const {Title, Text} = Typography;

const discountCodeService = new DiscountCodeService();

export const DiscountCodeTable = () => {
  const history = useHistory();

  const [codes, setCodes] = useState<NewDiscountCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    current,
    setCurrent,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    paginationSelector,
  } = useTablePagination({itemName: 'codes'});

  const searchTimeout = useRef<any>();

  const {packages, levels, curriculums, permission, isLoadingMeta, loadLevels} =
    useDiscountCodeData();

  // ── Fetch ──────────────────────────────────────────────────────
  const fetchCodes = (page: number, limit: number, search?: string) => {
    if (permission !== 'admin') {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setIsError(false);

    discountCodeService
      .getAllDiscountCodes(page, limit)
      .then(resp => {
        setCodes(resp.items);
        setTotal(resp.total);
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (permission !== undefined) {
      fetchCodes(current, pageSize, searchText || undefined);
    }
  }, [current, pageSize, permission]);

  // ── Search ─────────────────────────────────────────────────────
  const onSearch = (value: string) => {
    setSearchText(value);
    setCurrent(1);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchCodes(1, pageSize, value || undefined);
    }, 600);
  };

  // ── Delete ─────────────────────────────────────────────────────
  const handleDelete = async (record: NewDiscountCode) => {
    setDeletingId(record._id);
    try {
      await discountCodeService.deleteDiscountCode(record._id);
      notification.success({
        message: 'Deleted',
        description: `"${record.code}" was permanently deleted.`,
        duration: 3,
      });
      fetchCodes(current, pageSize, searchText || undefined);
    } catch (err: any) {
      notification.error({
        message: 'Delete Failed',
        description: err?.message || 'An unexpected error occurred.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ── Navigate to details ────────────────────────────────────────
  const goToDetails = (record: NewDiscountCode) => {
    history.push(`/discountcodes/${record._id}`, {code: record});
  };

  // ── Columns ────────────────────────────────────────────────────
  const columns: any[] = [
    {
      title: 'Code',
      dataIndex: 'code',
      sorter: (a: NewDiscountCode, b: NewDiscountCode) =>
        a.code.localeCompare(b.code),
      render: (code: string) => (
        <Text strong style={{fontFamily: 'monospace', fontSize: 13}}>
          {code}
        </Text>
      ),
    },
    {
      title: 'Value',
      dataIndex: 'value',
      render: (value: number, record: NewDiscountCode) => (
        <Tag color="blue">
          {value}
          {record.type === 'PERCENTAGE' ? '%' : ' $'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: [
        {text: 'Active', value: 'active'},
        {text: 'Expired', value: 'expired'},
      ],
      onFilter: (val: any, record: NewDiscountCode) => record.status === val,
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'error'}>
          {status?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      defaultSortOrder: 'descend',
      sorter: (a: NewDiscountCode, b: NewDiscountCode) =>
        moment(a.expiryDate).unix() - moment(b.expiryDate).unix(),
      render: (date: string) => {
        const isExpired = moment(date).isBefore(moment());
        return (
          <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm:ss')}>
            <Text type={isExpired ? 'danger' : 'secondary'}>
              {moment(date).fromNow()}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      filters: [
        {text: 'Percentage', value: 'percentage'},
        {text: 'Fixed', value: 'fixed'},
      ],
      onFilter: (val: any, record: NewDiscountCode) => record.type === val,
      render: (type: string) => (
        <Tag>{type === 'percentage' ? '% Percentage' : '$ Fixed'}</Tag>
      ),
    },
    {
      title: 'Students Used',
      dataIndex: 'studentIds',
      render: (ids: string[]) => (
        <Tag color={ids?.length > 0 ? 'orange' : 'default'}>
          {ids?.length ?? 0} student{ids?.length !== 1 ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Partner',
      dataIndex: 'partnerId',
      render: (partner: string) =>
        partner ? (
          <Tag color="purple">{partner}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      render: (by: string) => (
        <Tooltip title={by}>
          <Text style={{fontSize: 12}}>{by?.split('@')[0] ?? by}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      sorter: (a: NewDiscountCode, b: NewDiscountCode) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      render: (date: string) => (
        <Tooltip title={moment(date).format('YYYY-MM-DD HH:mm')}>
          <Text type="secondary" style={{fontSize: 12}}>
            {moment(date).fromNow()}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_: any, record: NewDiscountCode) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={e => {
                e.stopPropagation();
                goToDetails(record);
              }}
            />
          </Tooltip>
          <Popconfirm
            title={`Delete "${record.code}"? This cannot be undone.`}
            okText="Delete"
            okButtonProps={{danger: true}}
            cancelText="Cancel"
            onConfirm={e => {
              e?.stopPropagation();
              handleDelete(record);
            }}>
            <Tooltip title="Delete">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deletingId === record._id}
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────
  if (permission && permission !== 'admin') {
    return (
      <Alert
        type="warning"
        message="Access Restricted"
        description="You don't have permission to view discount codes."
        style={{margin: 24}}
      />
    );
  }

  return (
    <div style={{padding: '16px 24px'}}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}>
        <Title level={3} style={{margin: 0}}>
          Discount Codes
          {!isLoading && total > 0 && (
            <Text
              type="secondary"
              style={{fontSize: 14, fontWeight: 400, marginLeft: 8}}>
              ({total} total)
            </Text>
          )}
        </Title>

        <Space>
          {paginationSelector}

          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={() =>
                fetchCodes(current, pageSize, searchText || undefined)
              }
              loading={isLoading}
            />
          </Tooltip>

          {permission === 'admin' && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowModal(true)}>
              Create Code
            </Button>
          )}
        </Space>
      </div>

      {/* Search */}
      <div style={{marginBottom: 16}}>
        <Input
          prefix={<SearchOutlined style={{color: '#ccc'}} />}
          placeholder="Search by code name..."
          allowClear
          style={{maxWidth: 320}}
          onChange={e => onSearch(e.target.value)}
        />
      </div>

      {/* Error */}
      {isError && (
        <Alert
          type="error"
          message="Failed to load discount codes"
          action={
            <Button size="small" onClick={() => fetchCodes(current, pageSize)}>
              Retry
            </Button>
          }
          style={{marginBottom: 16}}
        />
      )}

      {/* Table */}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={codes}
        loading={isLoading || isLoadingMeta}
        scroll={codes?.length ? {x: 800} : undefined}
        pagination={paginationConfig}
        onRow={record => ({
          onClick: () => goToDetails(record),
          style: {cursor: 'pointer'},
        })}
      />

      {/* Create Modal */}
      {permission === 'admin' && (
        <CreateDiscountCodeModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setCurrent(1);
            fetchCodes(1, pageSize);
          }}
          packages={packages}
          curriculums={curriculums}
          levels={levels}
          onNeedLevels={loadLevels}
          createDiscountCode={input =>
            discountCodeService.createDiscountCode(input as any)
          }
        />
      )}
    </div>
  );
};
